import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { auth } from "@/authentication/Firebase.jsx";
import { config } from "@/config/Config.js";
import { ACCESS_CHANGED_EVENT } from "@/authentication/accessChange.js";

const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;

/**
 * Subscribes to the notification SSE stream at {@code GET /v1/notifications/stream}.
 *
 * Implementation note: the browser {@code EventSource} API can't set an
 * {@code Authorization} header, so we use {@code fetch} with a streaming
 * body and parse SSE frames manually. On disconnect we reconnect with
 * exponential backoff (1→2→4→8→16→30s cap), matching the server-side
 * heartbeat cadence.
 *
 * @param {(event: {id: string, notification: object}) => void} onNotification
 *        Called when the server emits an `event: notification` frame.
 * @param {boolean} [enabled=true] — pass {@code false} to pause (logged out).
 */
export function useNotificationStream(onNotification, enabled = true) {
    const orgId = useSelector((state) => state.dashboardContext?.selectedOrgId);
    const handlerRef = useRef(onNotification);

    useEffect(() => {
        handlerRef.current = onNotification;
    }, [onNotification]);

    useEffect(() => {
        if (!enabled || !orgId) return undefined;

        let abort = new AbortController();
        let closed = false;
        let backoff = INITIAL_BACKOFF_MS;
        let reconnectTimer = null;

        const connect = async () => {
            if (closed) return;
            // Don't even try to open the stream while the tab is hidden -
            // Chrome's network suspension on backgrounded pages will fail
            // the fetch with ERR_NETWORK_IO_SUSPENDED and log noise on
            // every retry. The visibilitychange listener resumes us when
            // the tab becomes visible again.
            if (typeof document !== "undefined" && document.hidden) return;
            abort = new AbortController();
            try {
                const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
                if (!token) {
                    scheduleReconnect();
                    return;
                }
                const response = await fetch(`${config.serverBaseUrl}/v1/notifications/stream`, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: "text/event-stream",
                        "X-Web-Org-Id": orgId,
                    },
                    signal: abort.signal,
                    credentials: "include",
                });
                if (!response.ok || !response.body) {
                    throw new Error(`SSE connect failed: ${response.status}`);
                }

                backoff = INITIAL_BACKOFF_MS;
                const reader = response.body.getReader();
                const decoder = new TextDecoder("utf-8");
                let buffer = "";

                while (!closed) {
                    const { value, done } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });

                    let sep;
                    while ((sep = buffer.indexOf("\n\n")) !== -1) {
                        const frame = buffer.slice(0, sep);
                        buffer = buffer.slice(sep + 2);
                        dispatchFrame(frame);
                    }
                }
            } catch (err) {
                if (closed || abort.signal.aborted) return;
                // Backgrounded-tab network suspension is expected and silent;
                // visibilitychange will reconnect us. Log only the genuinely
                // unexpected transport failures.
                if (!isSuspendedTransportError(err)) {
                    console.warn("Notification stream error, reconnecting:", err?.message || err);
                }
            }
            if (!closed) scheduleReconnect();
        };

        const dispatchFrame = (frame) => {
            // Parse minimal SSE: collect `event:` and `data:` lines, ignore comments.
            let eventType = "message";
            const dataLines = [];
            let id = null;
            for (const rawLine of frame.split("\n")) {
                const line = rawLine.trimEnd();
                if (!line || line.startsWith(":")) continue;
                if (line.startsWith("event:")) eventType = line.slice(6).trim();
                else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^\s/, ""));
                else if (line.startsWith("id:")) id = line.slice(3).trim();
            }
            // "Your access changed" (AccessChangeSignal): not a bell card - re-dispatched for useAccessRefresh, which
            // re-reads the user's permissions in place. Two subscribers may see it; the refresh dedups by id.
            if (eventType === "access-changed") {
                let detail = {};
                try { detail = dataLines.length ? JSON.parse(dataLines.join("\n")) : {}; } catch (_) { /* id only */ }
                try { window.dispatchEvent(new CustomEvent(ACCESS_CHANGED_EVENT, { detail: { id: id || detail.id, ...detail } })); } catch (_) { /* teardown */ }
                return;
            }
            if (eventType !== "notification" || dataLines.length === 0) return;
            try {
                const payload = JSON.parse(dataLines.join("\n"));
                handlerRef.current?.({ id, notification: payload });
            } catch (e) {
                console.warn("Failed to parse SSE data frame", e);
            }
        };

        const scheduleReconnect = () => {
            if (closed) return;
            // Skip the timer entirely while the tab is hidden; visibilitychange
            // will fire `connect()` directly when we come back to foreground.
            if (typeof document !== "undefined" && document.hidden) return;
            reconnectTimer = setTimeout(connect, backoff);
            backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
        };

        // Resume the stream when the tab becomes visible again. Aborting any
        // in-flight request first prevents double-connects if a previous
        // hidden-time fetch was still pending.
        const onVisibilityChange = () => {
            if (closed || document.hidden) return;
            backoff = INITIAL_BACKOFF_MS;
            if (reconnectTimer) {
                clearTimeout(reconnectTimer);
                reconnectTimer = null;
            }
            abort.abort();
            connect();
        };
        if (typeof document !== "undefined") {
            document.addEventListener("visibilitychange", onVisibilityChange);
        }

        connect();

        return () => {
            closed = true;
            if (reconnectTimer) clearTimeout(reconnectTimer);
            if (typeof document !== "undefined") {
                document.removeEventListener("visibilitychange", onVisibilityChange);
            }
            abort.abort();
        };
    }, [enabled, orgId]);
}

/**
 * True when the fetch failure is a backgrounded-page network suspension
 * (Chrome's ERR_NETWORK_IO_SUSPENDED, or the generic "Failed to fetch" that
 * the same condition surfaces on some browsers). These aren't real outages -
 * the visibilitychange handler will reconnect when the tab is visible again.
 */
function isSuspendedTransportError(err) {
    const message = err?.message || String(err || "");
    return (
        (typeof document !== "undefined" && document.hidden) ||
        message.includes("ERR_NETWORK_IO_SUSPENDED") ||
        message === "Failed to fetch"
    );
}
